/**
 * TwoFactor Component
 *
 * Description: This component handles the Two-Factor Authentication (2FA) process. It includes a form for entering a 4-digit OTP sent to the user's registered email, and a countdown timer for resending the code. It also manages the loading state during the verification process.
 *
 * File Name: TwoFactor.tsx
 * Date: 2024-07-22
 */

import React, { useEffect, useState, FormEvent } from "react";
import VerificationInput from "react-verification-input";
import CircleButton from "src/Components/GlobalComponents/CircleButton";
import moment from "moment";
import httpRequest from "../../helpers/httpRequest";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { removeAuth, setAuth, setUserDetails } from "../../Store/reducer";
import { twoFactorKey } from "../../lib/constans";
import { AppDispatch } from "@store/store";
import { removeToken } from "src/helpers/helperFunctions";
import { useNavigate } from "react-router-dom";
import { getIndicators } from "src/Store/redDots";
import { removeAllIndicators } from "src/Store/redDots";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ItwoFactor {
  keepMeLogedIn: boolean;
  otpExpire: string;
  userName: string;
  [key: string]: any; // Allow for any additional properties
}

// Define the response type based on the expected structure
interface IOTPResponse {
  otpExpire?: string;
  message?: string;
  [key: string]: any; // Allow for any additional properties
}

interface ItokenReceived {
  token: string;
}

const TwoFactor: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string>(""); // State to store the OTP entered by the user
  const [isLoader, setIsLoader] = useState<boolean>(false); // State to handle loading indicator

  const [timeLeft, setTimeLeft] = useState<string>("00:00"); // Time left in seconds
  const [userName, setUserName] = useState<string>(""); // State to store the username
  const [keepMeLogedIn, setKeepMeLogedIn] = useState<boolean>(false); // State to store "Keep me logged in" checkbox status

  const [bool, setBool] = useState<boolean>(false); // State to trigger timer updates
  const [recentLoading, setRecentLoading] = useState<boolean>(false);

  // API_CALL: API call for resent OTP
  const resendOtp = async () => {
    setRecentLoading(true);
    const { res, err } = await httpRequest<IOTPResponse>({
      method: "post",
      path: "/auth/recentOtp",
      params: { userName },
    });
    if (res) {
      let { message = "" } = res;
      toast(message, { type: "success" });

      // Store relevant information in sessionStorage
      let obj = {
        userName,
        keepMeLogedIn,
        otpExpire: moment().add(2, "minutes").toISOString(),
      };
      sessionStorage.setItem(twoFactorKey, JSON.stringify(obj));

      setBool((prev) => !prev); // Trigger timer update
    } else {
      toast(err, { type: "error" });
    }
    setRecentLoading(false);
  };

  const fetchProfile = async (_token: string) => {
    const { res, err } = await httpRequest<any>({
      path: "/operators/profile",
      token: _token,
    });
    if (res) {
      dispatch(setUserDetails(res));
    } else {
      toast(err, { type: "error" });
      dispatch(removeAuth()); // logout
      dispatch(removeAllIndicators());
      removeToken();
      navigate("/admin/login");
    }
  };

  // API_CALL: API call for verifying the OTP
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!otp || otp.length < 4) {
      toast("OTP is required!", { type: "error" });
      return;
    }
    setIsLoader(true);
    const { res, err } = await httpRequest<ItokenReceived>({
      method: "post",
      path: "/auth/verfifcation",
      params: { otp, userName, keepMeLogedIn },
    });
    if (res) {
      dispatch(setAuth(res?.token));
      console.log("res", res);
      console.log("res?.token", res?.token);
      dispatch(getIndicators("/operators/admin-indicators"));
      fetchProfile(res?.token);
      navigate("/admin");
      sessionStorage.removeItem(twoFactorKey);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  // Function to format time in MM:SS format
  const formatTime = (seconds: string) => {
    // Convert seconds to a number and handle invalid cases
    const totalSeconds = Number(seconds);
    if (isNaN(totalSeconds) || totalSeconds < 0) {
      return "00:00"; // Return default time format if conversion fails
    }
    const minutes = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${minutes.toString().padStart(2, "0") || "00"}:${
      secs.toString().padStart(2, "0") || "00"
    }`;
  };

  // Effect to handle OTP expiration timer
  useEffect(() => {
    const storedData = sessionStorage.getItem(twoFactorKey);
    let info: ItwoFactor | null = null;

    if (storedData) {
      try {
        info = JSON.parse(storedData) as ItwoFactor;
      } catch (error) {
        console.error("Failed to parse session storage data:", error);
      }
    }
    if (!info) {
      navigate(-1); // Navigate back if no session info
    } else {
      setUserName(info?.userName);
      setKeepMeLogedIn(info?.keepMeLogedIn);
      const otpExpire = moment(info.otpExpire); // Assuming otpExpire is stored as a timestamp
      const interval = setInterval(() => {
        const now = moment();
        const diff = otpExpire.diff(now);
        if (diff <= 0) {
          setTimeLeft("00:00"); // Time has expired
          clearInterval(interval);
        } else {
          setTimeLeft(String(moment.duration(diff).asSeconds())); // Update time left
        }
      }, 1000);

      return () => clearInterval(interval); // Clean up interval on component unmount
    }
  }, [bool]);

  return (
    <div className="customer-login-main">
      <div className="container-1600">
        <div className="customer-login-main-inner">
          <h1 className="font-Poppins-SemiBold font-size-32px theme-color-green leading-none text-center">
            Verification code
          </h1>
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <div className="customer-login-main-inner-main items-center">
              <div className="verification-code">
                <VerificationInput
                  autoFocus={true}
                  length={4}
                  validChars="0-9"
                  placeholder="0"
                  value={otp}
                  onChange={(e) => setOtp(e)}
                />
              </div>
              <div className="flex gap-1">
                <span className="font-size-16px text-white font-Poppins-Regular">
                  {formatTime(timeLeft)}
                </span>
                <button
                  type="button"
                  disabled={timeLeft !== "00:00" || recentLoading}
                  onClick={resendOtp}
                  className="text-[#2CDED3] flex items-center gap-1 font-size-16px disabled:opacity-50 disabled:cursor-not-allowed font-Poppins-SemiBold"
                  id="resend-otp-btn"
                >
                  {recentLoading ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin font-size-16px"
                      />{" "}
                      Resending...
                    </>
                  ) : (
                    "Resend Code"
                  )}
                </button>
              </div>
              <div className="login-btn-home-banner-parent flex items-center justify-center">
                <CircleButton
                  ButtonText={"Verify"}
                  loader={isLoader}
                  type={"submit"}
                  id="verify-otp-btn"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TwoFactor;
